import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELDS = [
  { key: "name", label: "Nom et prénom (ou nom commercial)", ph: "ex. Jérôme Rognin-Picard" },
  { key: "siren", label: "SIREN (9 chiffres)", ph: "ex. 492 006 234" },
  { key: "rcs", label: "Immatriculation", ph: "ex. Immatriculé au RNE le 27/10/2026" },
  { key: "siret", label: "SIRET (14 chiffres, facultatif : remplace le SIREN si rempli)", ph: "ex. 492 006 234 00024" },
  { key: "host_address", label: "Adresse postale de l'hébergeur (Emergent, fournie par support@emergent.sh)", ph: "ex. 123 Main Street, San Francisco, CA, USA" },
];

// Admin: seller identity used on invoices, CGV and mentions légales.
export function AdminLegalInfo() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/legal").then(({ data }) => setForm(data)).catch(() => setForm(null));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/legal", { name: form.name, address: form.address, siret: form.siret, siren: form.siren, rcs: form.rcs, host_address: form.host_address });
      setForm(data);
      toast.success("Infos légales enregistrées.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace("Value error, ", "") : d || "Échec de l'enregistrement.");
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;
  const complete = form.name && form.address && (form.siret || form.siren);
  const input = "w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]";
  return (
    <form onSubmit={save} className="border border-[#002FA7] p-6 max-w-3xl mt-6" data-testid="admin-legal-form">
      <div className="sux-marker mb-2 flex items-center gap-2"><Landmark className="w-3 h-3" /> Infos légales<InfoTip testid="admin-legal-info" content={"Nom, adresse, SIREN et immatriculation (repris du Kbis). Ils s'affichent sur les factures, les devis, les CGV et en bas de toutes les pages. Ajoutez le SIRET quand l'INSEE vous l'aura envoyé."} /></div>
      <p className="text-sm mb-4" data-testid="admin-legal-status">
        {complete
          ? <b className="text-[#0a7a2f]">Complet : les achats génèrent de vraies factures (F-…).</b>
          : <b className="text-[#FF2A00]">Incomplet : les achats génèrent des reçus (R-…) et les CGV affichent « À compléter ».</b>}
      </p>
      {FIELDS.map((f) => (
        <label key={f.key} className="block mb-3">
          <span className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground block mb-1">{f.label}</span>
          <input className={input} value={form[f.key]} placeholder={f.ph} data-testid={`admin-legal-${f.key}-input`}
            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
        </label>
      ))}
      <label className="block mb-4">
        <span className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground block mb-1">Adresse professionnelle</span>
        <textarea rows={2} className={input} value={form.address} placeholder={"ex. 1 rue de la Paix\n38470 Vinay"} data-testid="admin-legal-address-input"
          onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </label>
      <button type="submit" disabled={busy} data-testid="admin-legal-save-btn"
        className="font-mono uppercase tracking-[0.2em] text-[11px] px-6 py-3 text-white bg-[#002FA7] hover:bg-[#00227A] transition-colors disabled:opacity-50 flex items-center gap-2">
        {busy && <Loader2 className="w-3 h-3 animate-spin" />} Enregistrer
      </button>
      <p className="mt-3 text-xs text-muted-foreground font-mono">
        Mis à jour instantanément sur les nouvelles factures, les devis, la page CGV et les mentions légales.
      </p>
    </form>
  );
}

export default AdminLegalInfo;
